import { AlertVariant } from 'react-unity';
import FormModel from '../../utils/FormModel';
import { IStateForm, AbstractFormState, AbstractForm } from '../common/AbstractForm';
import PortOpeningRequestOnPrem from '../../models/entities/PortOpeningRequestOnPrem';
import UserLookupObject from '../../models/UserLookupObject';
import { LinkTreeItem } from '../common/LinkTree';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import { Params, Location } from 'react-router-dom';
import OnPremPortOpeningRequestService from '../../services/OnPremPortOpeningRequestService';
import PortOpeningRuleOnPrem from '../../models/entities/PortOpeningRuleOnPrem';
import CartaId from '../../models/entities/CartaId';
import { authenticatedUser } from '../../authentication/authModule';
import EnvironmentConfiguration from '../../utils/EnvironmentConfiguration';

interface EndModal extends AlertBanner {
	redirectionLink?: string;
}

export interface AbstractPORFormProps {
	params?: Params<string>;
	navigate?: any;
	location?: Location;
}

export interface IPORStateForm extends IStateForm {
	selectedScope?: string;
	contact?: UserLookupObject | null;
	approver?: UserLookupObject | null;
	businessJustification: string;
	connectionId?: number;
	comments?: string;
	ruleList: PortOpeningRuleOnPrem[];
	changeTicketId?: string;
	ownerDelegate?: string;
	cartaIds: CartaId[];
}

export interface AbstractPORFormState<T> extends AbstractFormState<T> {
	operationInProgress: boolean;
	endModal: EndModal;
	submissionWarned: boolean;
	submissionAlert: AlertBanner;

	approverDelegateOptions?: any;
	loadingTracker?: boolean;
	failedTracker?: boolean,
	errorTracker?: string; 
}

export abstract class OnPremAbstractPORForm<
	Props extends AbstractPORFormProps,
	State extends AbstractPORFormState<IPORStateForm>
	> extends AbstractForm<Props, State> {

	portOpeningRequestServiceOnPrem: OnPremPortOpeningRequestService;
	lastTrackerId?: number;
	
	constructor(props) {
		super(props);
		this.portOpeningRequestServiceOnPrem = new OnPremPortOpeningRequestService();
	}

	getLinkTree = (): LinkTreeItem[] => {
		return [
			{ text: 'Port Opening Requests', to: '/onPremPortOpeningRequests' },
		];
	};

	initFormModel() {
		this.formModel = new FormModel({
			contactId: {
				getValue: () => this.state.stateForm.contact?.id,
			},
			approverId: {
				// getValue: () => this.state.stateForm.approver?.id,
				// validation: {
				// 	required: true,
				// }
				getValue: () =>
					this.state.stateForm?.selectedScope === PortOpeningRequestsDirection.DomainToConnection.name ? this.state.stateForm.ownerDelegate: this.state.stateForm.approver?.id,
				validation: {
					required: false
				}
			},
			connectionId: {
				getValue: () => this.state.stateForm.connectionId
			},
			changeTicketId: {
				getValue: () => this.state.stateForm.changeTicketId,				
			},
			businessJustification: {
				getValue: () => this.state.stateForm.businessJustification,
				validation: {
					required: true,
					rules: [
						{
							assert: () =>
								this.state.stateForm.businessJustification?.trim().length > 4,
							message: 'Justification must contain at least 5 characters.',
						},
					],
				},
			},			
			portOpeningRules: {
				getValue: () => this.state.stateForm.ruleList,
				validation: {
					required: true
				}
			},
			comments: {
				getValue: () => this.state.stateForm.comments,
			},
			ownerDelegate: {
				getValue: () => this.state.stateForm.ownerDelegate,
			},
			cartaIds: {
				getValue: () => this.state.stateForm.cartaIds,
				validation: {
					//required: true,
					rules: [{
						assert: () => {
							if (this.state.stateForm.cartaIds.every(x => x.cartaAppId.length > 0
								&& x.cartaAppName.length > 0))
								return true;
							if (this.state.stateForm.cartaIds.length === 1)
								return (this.state.stateForm.cartaIds[0].comment.length > 0
									&& this.state.stateForm.cartaIds[0].cartaAppId.length === 0);
							return false;
						},
						message: '',
					}]
				},
			},
		});
	}

	stateFormHandler = () => this.getStateFormHandlers();

	getStateFormHandlers() {
		return {
			contact: {
				value: this.state.stateForm.contact?.userPrincipalName,
				validation: this.formModel.fields.contactId.validation,
				onChange: (value) => {
					this.handleStateFormChange('contact', value);
				}
			},
			approver: {
				value: this.state.stateForm.approver?.mail ?? this.state.stateForm.approver?.userPrincipalName,
				validation: this.formModel.fields.approverId.validation,
				onChange: (value) => {
					this.setState({
						submissionWarned: false,
					});
					this.handleStateFormChange('approver', value);
				},
				higherApproval: false
			},
			ownerDelegate: {
				value: this.state.stateForm.ownerDelegate,
				validation: this.formModel.fields.approverDelegate?.validation,
				options: this.state.approverDelegateOptions ?? [],
				onChange: (event) => {
					this.handleStateFormChange('ownerDelegate', event.target.value);
				},
			},
			connectionId: {
				value: this.state.stateForm.connectionId,
				validation: this.formModel.fields.connectionId?.validation,
				onChange: async (event) => {
					const trackerId = event.target.value;

					this.handleStateFormChange('connectionId', trackerId);

					if (
						trackerId.length >= 5 &&
						!this.state.loadingTracker
					) {
						this.handleApproverDelegateOptions(Number(trackerId));
					}

					this.setState({ failedTracker: false });
				},
			},
			businessJustification: {
				value: this.state.stateForm.businessJustification,
				validation: this.formModel.fields.businessJustification.validation,
				onChange: (event) => {
					this.handleStateFormChange('businessJustification', event.target.value);
				}
			},
			ruleset: {
				rules: this.state.stateForm.ruleList,
				onChange: (rules: PortOpeningRuleOnPrem[]) => {
					this.handleStateFormChange('ruleList', rules);
				}
			},
			comments: {
				value: this.state.stateForm.comments || '',
				validation: this.formModel.fields.comments.validation,
				onChange: (event) => {
					this.handleStateFormChange('comments', event.target.value);
				}
			},
			changeTicketId: {
				value: this.state.stateForm.changeTicketId,
				validation: this.formModel.fields.changeTicketId.validation,
				onChange: (event) => {
					this.handleStateFormChange('changeTicketId', event.target.value);
				}
			},
			cartaIds: {
				value: this.state.stateForm.cartaIds,
				validation: this.formModel.fields.cartaIds.validation,
				onChange: (cartaIds) => {
					if (cartaIds.length === 1 && cartaIds[0]?.cartaAppId === this.state.stateForm.cartaIds[0]?.cartaAppId) {
						const newComment = cartaIds[0].comment;
						cartaIds[0] = this.state.stateForm.cartaIds[0];
						cartaIds[0].comment = newComment;
					}
					this.handleStateFormChange('cartaIds', cartaIds);
				}
			},
		};
	}

	handleEndModal = (text: string, variant: AlertVariant, timeout: number, link: string) => {
		this.setState({
			endModal: {
				visible: true,
				text,
				variant,
				redirectionLink: link
			}
		},
		() => {
			setTimeout(() => {
				this.endAndRedirect();
			}, timeout);
		});
	};

	endAndRedirect = () => {
		const link = this.state.endModal.redirectionLink;
		this.setState({
			endModal: { visible: false }
		},
		() => {
			this.props.navigate(link);
		});
	};

	setSubmissionAlertInProgress = (message) => {
		window.scroll(0, 0);
		this.setState({
			operationInProgress: true,
			submissionAlert: {
				visible: true,
				text: message
			}
		});
	};

	setSubmissionAlertError = (message) => {
		this.setState({
			submissionAlert: {
				visible: true,
				text: message,
				variant: 'error'
			}
		});
	};

	handleSubmissionAlertClose = () => {
		this.setState({
			submissionAlert: {
				visible: false
			}
		});
	};

	handleApproverDelegateOptions = async (trackerId: number) => {
		if (trackerId === this.lastTrackerId)
			return;
		try {
			this.setState({ loadingTracker: true });

			const networkConnection = await this.portOpeningRequestServiceOnPrem.getNCDBbyTrackerId(trackerId);
			//const networkConnection = await this.OnPremObjectRequestService.getNCDBbyTrackerId(trackerId);
			// const firewallOptions = [];
			// if (networkConnection.childName != null) {
			// 	networkConnection.childName.forEach(child => {
			// 		firewallOptions.push({ value: child, text: child })
			// 	})
			// }

			// if (fromReview) firewallOptions.push({ value: networkConnection.ncp, text: networkConnection.ncp })

			this.lastTrackerId = trackerId;
			const options = [];

			if (EnvironmentConfiguration.isDev()) {
				options.push({ value: '49d5da85-f104-440d-b6e8-1ae75a7802b4', text: 'Xu, Josh' })
			}
			else {
				if (networkConnection.owner?.id != null && networkConnection.owner?.id != authenticatedUser.userId)
					options.push({ value: networkConnection.owner.id, text: networkConnection.owner.displayName })

				// check if this is necessary or not
				networkConnection.delegate.filter(x => x.id != authenticatedUser.userId).forEach(element => {
					options.push({ value: element.id, text: element.displayName })
				});
			}
			this.setState({ approverDelegateOptions: options });
			//this.setState({ firewallOptions: firewallOptions })
			//this.handleStateFormChange('firewall', networkConnection.ncp);

			return options;
		}
		catch (err) {
			const HTTP_NOT_FOUND_ERROR_CODE = 404;
			if (err.status == HTTP_NOT_FOUND_ERROR_CODE)
				this.lastTrackerId = trackerId;
			this.handleStateFormChange('firewall', null);

			this.setState({ failedTracker: true });
			this.setState({ errorTracker: err?.response?.data?.message ?? 'There was an error while retrieving Tracker Id' });

			//this.setState({ firewallOptions: [] })
		}
		finally {
			this.setState({ loadingTracker: false });
		}
	}

	approverLevelVerified = async (): Promise<boolean> => {
		if (!this.state.submissionWarned) {
			try {
				this.setSubmissionAlertInProgress('Verifying...');
				const response = await this.portOpeningRequestServiceOnPrem.verifyApproverLevel(
					this.state.stateForm?.approver?.mail ?? this.state.stateForm?.approver?.userPrincipalName, false);
				if (!response.allowed) {
					this.setState({
						operationInProgress: false,
						submissionWarned: true,
						submissionAlert: {
							visible: true,
							text: 'The Approver does not meet the minimum DOAG level required.',
							variant: 'warning',
						}
					});
				}

				return response.allowed;
			} catch (err) {
				this.setSubmissionAlertError('The approver could not be verified. Please, try again later.');
				this.setState({
					operationInProgress: false,
				});
				return false;
			}
		}
		return true;
	};

	requiresApproverVerification = (selectedScope: string) => {
		return selectedScope !== PortOpeningRequestsDirection.DomainToConnection.name;
	};

	// eslint-disable-next-line class-methods-use-this
	stateFormFromRequest(por: PortOpeningRequestOnPrem): IPORStateForm {
		return {
			contact: !!por.contactUser &&
				UserLookupObject.fromUser(por.contactUser),
			approver: !!por.approvingUser &&
				UserLookupObject.fromUser(por.approvingUser),
			connectionId: por.connectionId,
			businessJustification: por.businessJustification,
			ruleList: por.portOpeningRules,
			comments: por.comments,
			cartaIds: por.cartaId,
			changeTicketId: por.workflowInstance.currentWorkflowInstanceState.ticketId			
		};
	}
}
